import axios from "axios";
import { getWorkspaceCode } from "../../shared/utils/auth";
import utilsCommon from "../../shared/utils/common";
import { EcoCommonConfig, EcoUserInfo } from "../../types/auth";
import {
  WorkspaceDTO,
  WorkspacePaymentInfo,
  WorkspacePaymentHis,
  PlanInfo,
  CreateWorkspaceRequestDTO,
  UpdateWorkspaceStatusRequestDTO,
  DeleteWorkspaceRequestDTO,
  WorkgroupMemberRequest,
  workspaceMemberDTO,
  WorkspaceJoinDto,
} from "../store/workspace";
import config from "../../shared/config/config";
import { BasicReturnMsg } from "../../types/common";
import axiosNoToken from "../../shared/utils/axiosNoToken";

const GATEWAY_URL = config.servers.gateway.url;

interface WorkspaceApi {
  getWorkspaceList(): Promise<WorkspaceDTO[]>;
  getWorkspaceDetail(dsdCode: string): Promise<WorkspaceDTO>;
  getWorkspacePayMyList(): Promise<WorkspacePaymentInfo[]>;
  getWorkspacePayMyHistory(): Promise<WorkspacePaymentHis[]>;
  getWorkspacePayPlanList(): Promise<PlanInfo[]>;
  createWorkspace(req: CreateWorkspaceRequestDTO): Promise<WorkspaceDTO>;
  updateWorkspace(req: UpdateWorkspaceStatusRequestDTO): Promise<WorkspaceDTO>;
  deleteWorkspace(req: DeleteWorkspaceRequestDTO): Promise<BasicReturnMsg>;
  getWorkspaceMemberList(
    req: WorkgroupMemberRequest
  ): Promise<workspaceMemberDTO[]>;
  getWorkspaceInviteMember(
    user: workspaceMemberDTO
  ): Promise<workspaceMemberDTO>;
  getWorkspaceInviteAgainMember(req: {
    userId: string;
    dsdCode: string;
  }): Promise<BasicReturnMsg>;
  signupWorkspacMember(req: {
    user: WorkspaceJoinDto;
    withInvitation: boolean;
  }): Promise<workspaceMemberDTO>;
  updateWorkspaceMember(req: workspaceMemberDTO): Promise<workspaceMemberDTO>;
  verifyMemberMail(userId: string): Promise<workspaceMemberDTO>;
  deleteMember(req: {
    userIds: string[];
    dsdCode: string;
  }): Promise<BasicReturnMsg>;
  verifyMemberMailAgain(userId: string): Promise<BasicReturnMsg>;
  getWorkspaceConfig(): Promise<EcoCommonConfig>;
  getWorkspaceUserInfo(dsdCode: string): Promise<EcoUserInfo>;
}

class WorkspaceServerApi implements WorkspaceApi {
  async getWorkspaceList(): Promise<WorkspaceDTO[]> {
    const resp = await axios.post<WorkspaceDTO[]>(
      `${GATEWAY_URL}/wapi/tenancy/list/my`,
      {
        userId: utilsCommon.getMyId(),
      }
    );
    return Promise.resolve(resp.data);
  }
  async getWorkspaceDetail(dsdCode: string): Promise<WorkspaceDTO> {
    const resp = await axios.get<WorkspaceDTO>(
      `${GATEWAY_URL}/wapi/tenancy/info/${dsdCode}`
    );
    return Promise.resolve(resp.data);
  }
  async getWorkspacePayMyList(): Promise<WorkspacePaymentInfo[]> {
    const resp = await axios.get<WorkspacePaymentInfo[]>(
      `${GATEWAY_URL}/wapi/tenancy/pay/payment/mylist`
    );
    return Promise.resolve(resp.data);
  }
  async getWorkspacePayMyHistory(): Promise<WorkspacePaymentHis[]> {
    const resp = await axios.get<WorkspacePaymentHis[]>(
      `${GATEWAY_URL}/wapi/tenancy/pay/payment/myHistory`
    );
    return Promise.resolve(resp.data);
  }
  async getWorkspacePayPlanList(): Promise<PlanInfo[]> {
    const resp = await axios.get<PlanInfo[]>(
      `${GATEWAY_URL}/wapi/tenancy/pay/payment/plans`
    );
    return Promise.resolve(resp.data);
  }
  async createWorkspace(req: CreateWorkspaceRequestDTO): Promise<WorkspaceDTO> {
    const resp = await axiosNoToken.post<WorkspaceDTO>(
      `${GATEWAY_URL}/wapi/tenancy/create`,
      req
    );
    return Promise.resolve(resp.data);
  }
  async updateWorkspace(
    req: UpdateWorkspaceStatusRequestDTO
  ): Promise<WorkspaceDTO> {
    const resp = await axiosNoToken.post<WorkspaceDTO>(
      `${GATEWAY_URL}/wapi/tenancy/status`,
      req
    );
    return Promise.resolve(resp.data);
  }
  async deleteWorkspace(
    req: DeleteWorkspaceRequestDTO
  ): Promise<BasicReturnMsg> {
    const resp = await axiosNoToken.post<BasicReturnMsg>(
      `${GATEWAY_URL}/wapi/tenancy/delete`,
      req
    );
    return Promise.resolve(resp.data);
  }
  async getWorkspaceMemberList(
    req: WorkgroupMemberRequest
  ): Promise<workspaceMemberDTO[]> {
    const resp = await axios.post<workspaceMemberDTO[]>(
      req.isAdminPage
        ? `${GATEWAY_URL}/wapi/tenancy/member/admin/list`
        : `${GATEWAY_URL}/wapi/tenancy/member/list`,
      req.isAdminPage
        ? req
        : {
            searchOpt: req.searchOpt,
            dsdCode: getWorkspaceCode() ?? "",
            userType:
              !!req.internalPos && !!req.externalPos
                ? null
                : req.internalPos
                ? 1
                : 2,
            // tenancyCode: getWorkspaceCode() ?? '',
          }
    );
    return Promise.resolve(resp.data);
  }
  async getWorkspaceInviteMember(
    user: workspaceMemberDTO
  ): Promise<workspaceMemberDTO> {
    const resp = await axiosNoToken.post<workspaceMemberDTO>(
      `${GATEWAY_URL}/wapi/tenancy/member/invite`,
      user
    );
    return Promise.resolve(resp.data);
  }
  async getWorkspaceInviteAgainMember(req: {
    userId: string;
    dsdCode: string;
  }): Promise<BasicReturnMsg> {
    const resp = await axiosNoToken.post<BasicReturnMsg>(
      `${GATEWAY_URL}/wapi/tenancy/member/invite/resend`,
      req
    );
    return Promise.resolve(resp.data);
  }
  async signupWorkspacMember(req: {
    user: WorkspaceJoinDto;
    withInvitation: boolean;
  }): Promise<workspaceMemberDTO> {
    const url = req.withInvitation
      ? "/papi/tenancy/member/invite/register"
      : "/papi/tenancy/member/register";
    const resp = await axiosNoToken.post<workspaceMemberDTO>(
      `${GATEWAY_URL}${url}`,
      req.user
    );
    return Promise.resolve(resp.data);
  }
  async updateWorkspaceMember(
    req: workspaceMemberDTO
  ): Promise<workspaceMemberDTO> {
    const resp = await axiosNoToken.post<workspaceMemberDTO>(
      `${GATEWAY_URL}/wapi/tenancy/member/update`,
      req
    );
    return Promise.resolve(resp.data);
  }
  async deleteMember(req: {
    userIds: string[];
    dsdCode: string;
  }): Promise<BasicReturnMsg> {
    const resp = await axiosNoToken.post<BasicReturnMsg>(
      `${GATEWAY_URL}/wapi/tenancy/member/delete`,
      req
    );
    return Promise.resolve(resp.data);
  }
  async verifyMemberMail(userId: string): Promise<workspaceMemberDTO> {
    const resp = await axiosNoToken.post<workspaceMemberDTO>(
      `${GATEWAY_URL}/papi/tenancy/member/register/verify`,
      { userId }
    );
    return Promise.resolve(resp.data);
  }
  async verifyMemberMailAgain(userId: string): Promise<BasicReturnMsg> {
    const resp = await axiosNoToken.post<BasicReturnMsg>(
      `${GATEWAY_URL}/papi/tenancy/member/register/verify/resend`,
      { userId }
    );
    return Promise.resolve(resp.data);
  }
  async getWorkspaceConfig(): Promise<EcoCommonConfig> {
    const resp = await axios.get<EcoCommonConfig>(
      `${GATEWAY_URL}/wapi/tenancy/config`
    );
    return Promise.resolve(resp.data);
  }
  async getWorkspaceUserInfo(dsdCode: string): Promise<EcoUserInfo> {
    const resp = await axios.post<EcoUserInfo>(
      `${GATEWAY_URL}/wapi/tenancy/info/my`,
      {
        userId: utilsCommon.getMyId(),
      }
    );
    return Promise.resolve(resp.data);
  }
}

const workspaceApi: WorkspaceApi = new WorkspaceServerApi();
export default workspaceApi;
